import { TemplateHandler } from "easy-template-x";

/**
 * Central service for processing .docx document templates.
 * Used across the application wherever document generation from templates is needed.
 * Can be used standalone or embedded as a node in a process workflow.
 *
 * Architecture:
 * - AVAILABLE_FIELDS: defines all data fields per context with dot-path resolution
 * - PLACEHOLDER_CONTEXTS: derived from AVAILABLE_FIELDS, defines placeholder metadata per context
 * - DATA_SOURCE_CONFIG: maps contexts to Vuex store paths/actions for auto-fetching
 * - detectRequiredContexts(): analyzes placeholder mappings to determine which data sources are needed
 *
 * Process Node Interface (for future workflow engine):
 *   Input:  { templateId, placeholderMappings, records?, extraData? }
 *   Output: { blob: Blob, filename: string }
 *   Entry:  processAndDownload(filePath, records, outputFilename, extraData, placeholderMappings)
 *
 * Template placeholders use {TagName} syntax.
 * Image placeholders are placed as dummy images in Word with {TagName} as alt text.
 * Loop tags use {#LoopTag}...{/LoopTag} syntax.
 */

// ---------------------------------------------------------------------------
// USAGE CONTEXTS — where can templates be used in the application
// Each usage context references one or more placeholder contexts for available data fields.
// ---------------------------------------------------------------------------
export const USAGE_CONTEXTS = {
  "organization-chart": {
    label: "Personalübersicht",
    description: "Dokumente für alle aktiven Mitglieder",
    icon: "mdi-account-group",
    supportsMultipleRecords: true,
    placeholderContexts: ["member", "organization"],
  },
  "personnel-records": {
    label: "Personalstammdaten",
    description: "Dokumente für einzelne Mitglieder",
    icon: "mdi-account-card",
    supportsMultipleRecords: false,
    placeholderContexts: ["member", "personnelContacts", "organization"],
  },
  "duty-reports": {
    label: "Dienstverwaltung",
    description: "Dokumente für Dienstberichte",
    icon: "mdi-clipboard-text",
    supportsMultipleRecords: false,
    placeholderContexts: ["dutyReport", "organization"],
  },
  "time-sheets": {
    label: "Arbeitszeitnachweise",
    description: "Dokumente für Zeiterfassung",
    icon: "mdi-clock-outline",
    supportsMultipleRecords: false,
    placeholderContexts: ["timeSheet", "organization"],
  },
  "batch-processing": {
    label: "Stapelverarbeitung",
    description: "Massenexporte",
    icon: "mdi-file-multiple",
    supportsMultipleRecords: true,
    placeholderContexts: ["member", "organization"],
  },
};

// ---------------------------------------------------------------------------
// AVAILABLE DATA FIELDS — flat list of all fields per context
// Each field: key (unique, prefixed), label, description, group, path (dot-path on record)
// The "context" property on each field enables cross-context resolution.
// ---------------------------------------------------------------------------
export const AVAILABLE_FIELDS = {
  // ── Organization data (global, not per-record) ──────────────────────────
  organization: [
    {
      key: "Org_Name",
      label: "Name der Organisation",
      description: "Vollständiger Name der Organisation",
      group: "Organisation > Stammdaten",
      path: "longName",
      context: "organization",
    },
    {
      key: "Org_ShortName",
      label: "Kurzname",
      description: "Kurzname der Organisation",
      group: "Organisation > Stammdaten",
      path: "shortName",
      context: "organization",
    },
    {
      key: "Org_Department",
      label: "Abteilung/Fachbereich",
      description: "Abteilung oder Fachbereich der Organisation",
      group: "Organisation > Stammdaten",
      path: "department",
      context: "organization",
    },
    {
      key: "Org_StationNumber",
      label: "Wachennummer",
      description: "Wachennummer der Organisation",
      group: "Organisation > Stammdaten",
      path: "stationNumber",
      context: "organization",
    },
    {
      key: "Org_Street",
      label: "Straße",
      description: "Straße der Organisation",
      group: "Organisation > Adresse",
      path: "adress.street",
      context: "organization",
    },
    {
      key: "Org_PostalCode",
      label: "Postleitzahl",
      description: "Postleitzahl der Organisation",
      group: "Organisation > Adresse",
      path: "adress.postalCode",
      context: "organization",
    },
    {
      key: "Org_City",
      label: "Ort",
      description: "Ort der Organisation",
      group: "Organisation > Adresse",
      path: "adress.city",
      context: "organization",
    },
    {
      key: "Org_Email",
      label: "E-Mail",
      description: "E-Mail-Adresse der Organisation",
      group: "Organisation > Kontakt",
      path: "email",
      context: "organization",
    },
    {
      key: "Org_Phone",
      label: "Telefon",
      description: "Telefonnummer der Organisation",
      group: "Organisation > Kontakt",
      path: "phone",
      context: "organization",
    },
    {
      key: "Org_Fax",
      label: "Fax",
      description: "Faxnummer der Organisation",
      group: "Organisation > Kontakt",
      path: "fax",
      context: "organization",
    },
  ],

  // ── Member / Personnel records (general) ────────────────────────────────
  member: [
    {
      key: "FirstName",
      label: "Vorname",
      description: "Vorname des Mitglieds",
      group: "Personaldaten > Stammdaten",
      path: "user.firstName",
      context: "member",
    },
    {
      key: "LastName",
      label: "Nachname",
      description: "Nachname des Mitglieds",
      group: "Personaldaten > Stammdaten",
      path: "user.lastName",
      context: "member",
    },
    {
      key: "DisplayName",
      label: "Anzeigename",
      description: "Vollständiger Name des Mitglieds",
      group: "Personaldaten > Stammdaten",
      path: "user.displayName",
      context: "member",
    },
    {
      key: "Email",
      label: "E-Mail-Adresse",
      description: "E-Mail-Adresse des Mitglieds",
      group: "Personaldaten > Stammdaten",
      path: "user.email",
      context: "member",
    },
    {
      key: "PhoneNumber",
      label: "Telefonnummer",
      description: "Telefonnummer des Mitglieds",
      group: "Personaldaten > Stammdaten",
      path: "user.phoneNumber",
      context: "member",
    },
    {
      key: "Salutation",
      label: "Anrede",
      description: "Anrede des Mitglieds (z.B. Herr, Frau)",
      group: "Personaldaten > Stammdaten",
      path: "user.salutation",
      context: "member",
    },
    {
      key: "LetterSalutation",
      label: "Briefanrede",
      description: "Briefanrede (z.B. Sehr geehrter Herr ...)",
      group: "Personaldaten > Stammdaten",
      path: "user.letterSalutation",
      context: "member",
    },
    {
      key: "AcademicTitle",
      label: "Akadem. Titel",
      description: "Akademischer Titel (z.B. Dr., Prof.)",
      group: "Personaldaten > Stammdaten",
      path: "user.academicTitle",
      context: "member",
    },
    {
      key: "DateOfBirth",
      label: "Geburtsdatum",
      description: "Geburtsdatum des Mitglieds",
      group: "Personaldaten > Stammdaten",
      path: "user.dateOfBirth",
      context: "member",
    },
    {
      key: "BirthPlace",
      label: "Geburtsort",
      description: "Geburtsort des Mitglieds",
      group: "Personaldaten > Stammdaten",
      path: "user.birthPlace",
      context: "member",
    },
    {
      key: "Rank",
      label: "Dienstgrad",
      description: "Dienstgrad des Mitglieds",
      group: "Personaldaten > Dienstliche Daten",
      path: "rank.title",
      context: "member",
    },
    {
      key: "RankShort",
      label: "Dienstgrad (Kurz)",
      description: "Kurzbezeichnung des Dienstgrads",
      group: "Personaldaten > Dienstliche Daten",
      path: "rank.shortTitle",
      context: "member",
    },
    {
      key: "RankSince",
      label: "Dienstgrad seit",
      description: "Datum der Ernennung zum aktuellen Dienstgrad",
      group: "Personaldaten > Dienstliche Daten",
      path: "rank.since",
      context: "member",
    },
    {
      key: "Position",
      label: "Dienststellung",
      description: "Dienststellung/Funktion des Mitglieds",
      group: "Personaldaten > Dienstliche Daten",
      path: "dutyPosition.title",
      context: "member",
    },
    {
      key: "PositionShort",
      label: "Dienststellung (Kurz)",
      description: "Kurzbezeichnung der Dienststellung",
      group: "Personaldaten > Dienstliche Daten",
      path: "dutyPosition.shortTitle",
      context: "member",
    },
    {
      key: "PositionSince",
      label: "Dienststellung seit",
      description: "Datum der Übernahme der aktuellen Dienststellung",
      group: "Personaldaten > Dienstliche Daten",
      path: "dutyPosition.since",
      context: "member",
    },
    {
      key: "Group",
      label: "Gruppe",
      description: "Zugehörige Benutzergruppe",
      group: "Personaldaten > Zugehörigkeit",
      path: "group.title",
      context: "member",
    },
    {
      key: "Division",
      label: "Abteilung",
      description: "Zugehörige Abteilung",
      group: "Personaldaten > Zugehörigkeit",
      path: "division.title",
      context: "member",
    },
    {
      key: "DivisionShort",
      label: "Abteilung (Kurz)",
      description: "Kurzbezeichnung der Abteilung",
      group: "Personaldaten > Zugehörigkeit",
      path: "division.shortTitle",
      context: "member",
    },
    {
      key: "Unit",
      label: "Einheit",
      description: "Zugehörige Einheit",
      group: "Personaldaten > Zugehörigkeit",
      path: "unit.title",
      context: "member",
    },
    {
      key: "PersonnelNumber",
      label: "Personalnummer",
      description: "Personalnummer des Mitglieds",
      group: "Personaldaten > Dienstliche Daten",
      path: "user.personnelNumber",
      context: "member",
    },
    {
      key: "MemberSince",
      label: "Mitglied seit",
      description: "Datum des Beitritts",
      group: "Personaldaten > Dienstliche Daten",
      path: "user.memberSince",
      context: "member",
    },
    {
      key: "MemberUntil",
      label: "Mitglied bis",
      description: "Datum des Austritts",
      group: "Personaldaten > Dienstliche Daten",
      path: "user.memberUntil",
      context: "member",
    },
  ],

  // ── Personnel contacts (private/business/station contact data) ──────────
  personnelContacts: [
    {
      key: "PC_PrivateStreet",
      label: "Privatadresse – Straße",
      description: "Straße der privaten Anschrift",
      group: "Privatadresse",
      path: "personalContact.street",
      context: "personnelContacts",
    },
    {
      key: "PC_PrivateStreetNumber",
      label: "Privatadresse – Hausnummer",
      description: "Hausnummer der privaten Anschrift",
      group: "Personaldaten > Kontaktdaten > Privat",
      path: "personalContact.streetNumber",
      context: "personnelContacts",
    },
    {
      key: "PC_PrivatePostalCode",
      label: "Privatadresse – PLZ",
      description: "Postleitzahl der privaten Anschrift",
      group: "Personaldaten > Kontaktdaten > Privat",
      path: "personalContact.postalCode",
      context: "personnelContacts",
    },
    {
      key: "PC_PrivateCity",
      label: "Privatadresse – Ort",
      description: "Ort der privaten Anschrift",
      group: "Personaldaten > Kontaktdaten > Privat",
      path: "personalContact.city",
      context: "personnelContacts",
    },
    {
      key: "PC_PrivatePhone",
      label: "Privattelefon",
      description: "Private Telefonnummer",
      group: "Personaldaten > Kontaktdaten > Privat",
      path: "personalContact.phone",
      context: "personnelContacts",
    },
    {
      key: "PC_PrivateMobile",
      label: "Privathandy",
      description: "Private Mobilnummer",
      group: "Personaldaten > Kontaktdaten > Privat",
      path: "personalContact.mobile",
      context: "personnelContacts",
    },
    {
      key: "PC_PrivateEmail",
      label: "Private E-Mail",
      description: "Private E-Mail-Adresse",
      group: "Personaldaten > Kontaktdaten > Privat",
      path: "personalContact.email",
      context: "personnelContacts",
    },
    {
      key: "PC_BusinessCompany",
      label: "Arbeitgeber",
      description: "Name des Arbeitgebers",
      group: "Personaldaten > Kontaktdaten > Geschäftlich",
      path: "businessContact.company",
      context: "personnelContacts",
    },
    {
      key: "PC_BusinessStreet",
      label: "Geschäftsadresse – Straße",
      description: "Straße der geschäftlichen Anschrift",
      group: "Personaldaten > Kontaktdaten > Geschäftlich",
      path: "businessContact.street",
      context: "personnelContacts",
    },
    {
      key: "PC_BusinessCity",
      label: "Geschäftsadresse – Ort",
      description: "Ort der geschäftlichen Anschrift",
      group: "Personaldaten > Kontaktdaten > Geschäftlich",
      path: "businessContact.city",
      context: "personnelContacts",
    },
    {
      key: "PC_BusinessPhone",
      label: "Geschäftstelefon",
      description: "Geschäftliche Telefonnummer",
      group: "Personaldaten > Kontaktdaten > Geschäftlich",
      path: "businessContact.phone",
      context: "personnelContacts",
    },
    {
      key: "PC_BusinessEmail",
      label: "Geschäftliche E-Mail",
      description: "Geschäftliche E-Mail-Adresse",
      group: "Personaldaten > Kontaktdaten > Geschäftlich",
      path: "businessContact.email",
      context: "personnelContacts",
    },
    {
      key: "PC_StationPhone",
      label: "Wache – Telefon",
      description: "Telefonnummer an der Wache",
      group: "Personaldaten > Kontaktdaten > Wache",
      path: "stationContact.phone",
      context: "personnelContacts",
    },
    {
      key: "PC_StationMobile",
      label: "Wache – Mobilnummer",
      description: "Mobilnummer an der Wache",
      group: "Personaldaten > Kontaktdaten > Wache",
      path: "stationContact.mobile",
      context: "personnelContacts",
    },
    {
      key: "PC_StationEmail",
      label: "Wache – E-Mail",
      description: "E-Mail-Adresse an der Wache",
      group: "Personaldaten > Kontaktdaten > Wache",
      path: "stationContact.email",
      context: "personnelContacts",
    },
  ],

  // ── Duty report data ────────────────────────────────────────────────────
  dutyReport: [
    {
      key: "DR_Title",
      label: "Titel",
      description: "Titel des Dienstberichts",
      group: "Allgemein",
      path: "title",
      context: "dutyReport",
    },
    {
      key: "DR_Location",
      label: "Einsatzort",
      description: "Ort des Einsatzes/Dienstes",
      group: "Allgemein",
      path: "location",
      context: "dutyReport",
    },
    {
      key: "DR_Status",
      label: "Status",
      description: "Status des Dienstberichts",
      group: "Dienstbericht > Allgemein",
      path: "status",
      context: "dutyReport",
    },
    {
      key: "DR_Notes",
      label: "Anmerkungen",
      description: "Anmerkungen zum Dienstbericht",
      group: "Dienstbericht > Allgemein",
      path: "notes",
      context: "dutyReport",
    },
    {
      key: "DR_DurationInHours",
      label: "Dauer (Stunden)",
      description: "Dauer des Dienstes in Stunden",
      group: "Dienstbericht > Allgemein",
      path: "duration.inHours",
      context: "dutyReport",
    },
    {
      key: "DR_DurationInMinutes",
      label: "Dauer (Minuten)",
      description: "Dauer des Dienstes in Minuten",
      group: "Dienstbericht > Allgemein",
      path: "duration.inMinutes",
      context: "dutyReport",
    },
    {
      key: "DR_DutyType",
      label: "Dienstart",
      description: "Art des Dienstes",
      group: "Dienstbericht > Allgemein",
      path: "dutyType.title",
      context: "dutyReport",
    },
    {
      key: "DR_DutyTypeShort",
      label: "Dienstart (Kurz)",
      description: "Kurzbezeichnung der Dienstart",
      group: "Dienstbericht > Allgemein",
      path: "dutyType.shortTitle",
      context: "dutyReport",
    },
    {
      key: "DR_IncidentCode",
      label: "Einsatzstichwort",
      description: "Einsatzstichwort/Alarmierungscode",
      group: "Dienstbericht > Einsatzdaten",
      path: "incident.code",
      context: "dutyReport",
    },
    {
      key: "DR_IncidentNumber",
      label: "Einsatznummer",
      description: "Einsatznummer",
      group: "Dienstbericht > Einsatzdaten",
      path: "incident.number",
      context: "dutyReport",
    },
    {
      key: "DR_AttendeesTotal",
      label: "Teilnehmer (Gesamt)",
      description: "Gesamtzahl aller Teilnehmer",
      group: "Dienstbericht > Teilnehmer",
      path: "attendees.total",
      context: "dutyReport",
    },
    {
      key: "DR_AttendeesPresent",
      label: "Teilnehmer (Anwesend)",
      description: "Anzahl anwesender Teilnehmer",
      group: "Dienstbericht > Teilnehmer",
      path: "attendees.present",
      context: "dutyReport",
    },
    {
      key: "DR_AttendeesExcused",
      label: "Teilnehmer (Entschuldigt)",
      description: "Anzahl entschuldigter Teilnehmer",
      group: "Dienstbericht > Teilnehmer",
      path: "attendees.excused",
      context: "dutyReport",
    },
    {
      key: "DR_AttendeesUnexcused",
      label: "Teilnehmer (Unentschuldigt)",
      description: "Anzahl unentschuldigter Teilnehmer",
      group: "Dienstbericht > Teilnehmer",
      path: "attendees.unexcused",
      context: "dutyReport",
    },
    {
      key: "DR_TrainingSubject",
      label: "Ausbildungsthema",
      description: "Thema der Ausbildungseinheit",
      group: "Dienstbericht > Ausbildung",
      path: "training.subject",
      context: "dutyReport",
    },
    {
      key: "DR_AssignedTo",
      label: "Zuständig",
      description: "Zuständige Person",
      group: "Dienstbericht > Allgemein",
      path: "assignedTo.displayName",
      context: "dutyReport",
    },
  ],

  // ── Time sheet data ─────────────────────────────────────────────────────
  timeSheet: [
    {
      key: "TS_Title",
      label: "Bezeichnung",
      description: "Bezeichnung des Zeitnachweises",
      group: "Allgemein",
      path: "title",
      context: "timeSheet",
    },
    {
      key: "TS_Type",
      label: "Typ",
      description: "Typ des Zeitnachweises (z.B. Dienstzeit, Lehrgangszeit)",
      group: "Allgemein",
      path: "type",
      context: "timeSheet",
    },
    {
      key: "TS_Description",
      label: "Beschreibung",
      description: "Beschreibung des Zeitnachweises",
      group: "Allgemein",
      path: "description",
      context: "timeSheet",
    },
    {
      key: "TS_Status",
      label: "Status",
      description: "Bearbeitungsstatus",
      group: "Arbeitszeit > Allgemein",
      path: "status",
      context: "timeSheet",
    },
    {
      key: "TS_WorkedHours",
      label: "Arbeitsstunden",
      description: "Geleistete Arbeitszeit in Stunden",
      group: "Arbeitszeit > Zeiten",
      path: "workedTime.inHours",
      context: "timeSheet",
    },
    {
      key: "TS_WorkedMinutes",
      label: "Arbeitsminuten",
      description: "Geleistete Arbeitszeit in Minuten",
      group: "Arbeitszeit > Zeiten",
      path: "workedTime.inMinutes",
      context: "timeSheet",
    },
    {
      key: "TS_BillableHours",
      label: "Abrechenbare Stunden",
      description: "Abrechenbare Zeit in Stunden",
      group: "Arbeitszeit > Zeiten",
      path: "billableTime.inHours",
      context: "timeSheet",
    },
    {
      key: "TS_PayrollType",
      label: "Entgeltart",
      description: "Zugeordnete Entgeltart",
      group: "Arbeitszeit > Abrechnung",
      path: "payrollType.title",
      context: "timeSheet",
    },
    {
      key: "TS_ProjectTitle",
      label: "Projekt",
      description: "Zugeordnetes Projekt",
      group: "Arbeitszeit > Allgemein",
      path: "project.title",
      context: "timeSheet",
    },
    {
      key: "TS_UserDisplayName",
      label: "Mitarbeiter",
      description: "Name des zugeordneten Mitarbeiters",
      group: "Arbeitszeit > Allgemein",
      path: "user.displayName",
      context: "timeSheet",
    },
    {
      key: "TS_UserFirstName",
      label: "Vorname (Mitarbeiter)",
      description: "Vorname des zugeordneten Mitarbeiters",
      group: "Arbeitszeit > Allgemein",
      path: "user.firstName",
      context: "timeSheet",
    },
    {
      key: "TS_UserLastName",
      label: "Nachname (Mitarbeiter)",
      description: "Nachname des zugeordneten Mitarbeiters",
      group: "Arbeitszeit > Allgemein",
      path: "user.lastName",
      context: "timeSheet",
    },
  ],
};

// ---------------------------------------------------------------------------
// PLACEHOLDER CONTEXTS — derived from AVAILABLE_FIELDS
// Each context defines metadata + the placeholders available + the loop tag name
// ---------------------------------------------------------------------------
export const PLACEHOLDER_CONTEXTS = {
  organization: {
    label: "Organisationsdaten",
    description: "Stammdaten der Organisation (Name, Adresse, Kontaktdaten)",
    icon: "mdi-domain",
    isGlobal: true, // Not per-record, applied once globally
    placeholders: AVAILABLE_FIELDS.organization.map((f) => ({
      tag: f.key,
      label: f.label,
      description: f.description,
    })),
    loopTag: null, // No loop — always single
  },
  member: {
    label: "Personalstammdaten",
    description:
      "Daten von Mitgliedern (Name, Dienstgrad, Dienststellung, Gruppe, etc.)",
    icon: "mdi-account",
    isGlobal: false,
    placeholders: AVAILABLE_FIELDS.member.map((f) => ({
      tag: f.key,
      label: f.label,
      description: f.description,
    })),
    loopTag: "Members",
  },
  personnelContacts: {
    label: "Kontaktdaten (Personal)",
    description:
      "Private, geschäftliche und dienstliche Kontaktdaten von Mitgliedern",
    icon: "mdi-card-account-phone",
    isGlobal: false,
    placeholders: AVAILABLE_FIELDS.personnelContacts.map((f) => ({
      tag: f.key,
      label: f.label,
      description: f.description,
    })),
    loopTag: "Members", // Same loop as member — merged into member records
  },
  dutyReport: {
    label: "Dienstberichte",
    description:
      "Daten aus Dienstberichten (Titel, Ort, Dauer, Teilnehmer, etc.)",
    icon: "mdi-clipboard-text",
    isGlobal: false,
    placeholders: AVAILABLE_FIELDS.dutyReport.map((f) => ({
      tag: f.key,
      label: f.label,
      description: f.description,
    })),
    loopTag: "Reports",
  },
  timeSheet: {
    label: "Arbeitszeitnachweise",
    description:
      "Daten aus Zeitnachweisen (Arbeitszeit, Entgeltart, Projekt, etc.)",
    icon: "mdi-clock-outline",
    isGlobal: false,
    placeholders: AVAILABLE_FIELDS.timeSheet.map((f) => ({
      tag: f.key,
      label: f.label,
      description: f.description,
    })),
    loopTag: "TimeSheets",
  },
};

// ---------------------------------------------------------------------------
// DATA SOURCE CONFIG — maps contexts to Vuex store paths and actions
// Used by GenerateDocumentDialog to auto-fetch required data
// ---------------------------------------------------------------------------
export const DATA_SOURCE_CONFIG = {
  organization: {
    storeModule: "admin",
    statePath: "admin.activeOrganization",
    action: null, // Already loaded in app initialization
    isGlobal: true,
  },
  member: {
    storeModule: "admin",
    statePath: "admin.membershipsPublic",
    actionModule: "ADMIN",
    action: "GET_MEMBERSHIPS_PUBLIC",
    isGlobal: false,
  },
  personnelContacts: {
    storeModule: "admin",
    statePath: null, // Requires per-user fetch from personnelFile subcollection
    actionModule: null,
    action: null,
    isGlobal: false,
    requiresCustomFetch: true,
  },
  dutyReport: {
    storeModule: "dutyManagement",
    statePath: "dutyManagement.reports",
    actionModule: "DUTY_MANAGEMENT",
    action: "GET_REPORTS",
    isGlobal: false,
  },
  timeSheet: {
    storeModule: "accounting",
    statePath: "accounting.timeSheets",
    actionModule: "ACCOUNTING",
    action: "GET_TIME_SHEETS",
    isGlobal: false,
  },
};

// ---------------------------------------------------------------------------
// ALL_FIELDS_BY_KEY — flat lookup across all contexts for fast field resolution
// ---------------------------------------------------------------------------
export const ALL_FIELDS_BY_KEY = {};
Object.entries(AVAILABLE_FIELDS).forEach(([contextKey, fields]) => {
  fields.forEach((f) => {
    ALL_FIELDS_BY_KEY[f.key] = { ...f, context: contextKey };
  });
});

/**
 * Returns the effective list of placeholder contexts for a given primary context.
 * Always includes the primary context + "organization" (global).
 * For "member" context, also includes "personnelContacts" since the data can be merged.
 *
 * @param {string} primaryContext - The primary placeholder context stored on the template (e.g. "member")
 * @returns {string[]} - Array of context keys to use for field resolution
 */
export function getEffectiveContexts(primaryContext) {
  const contexts = new Set();

  // Always add organization as a global context
  contexts.add("organization");

  // Add primary context
  if (primaryContext) {
    contexts.add(primaryContext);
  }

  // member context also includes personnelContacts
  if (primaryContext === "member") {
    contexts.add("personnelContacts");
  }

  return Array.from(contexts);
}

/**
 * Returns the list of available data fields for one or more placeholder contexts.
 * @param {string|string[]} contexts - One or more context keys (e.g. "member" or ["member", "organization"])
 * @returns {Array} - Array of field definitions
 */
export function getAvailableFields(contexts) {
  if (!contexts) return [];
  const keys = Array.isArray(contexts) ? contexts : [contexts];
  const fields = [];
  keys.forEach((ctx) => {
    const ctxFields = AVAILABLE_FIELDS[ctx];
    if (ctxFields) fields.push(...ctxFields);
  });
  return fields;
}

/**
 * Returns available fields grouped by their `group` property, with context headers.
 * Accepts a single context string or an array of context strings.
 * @param {string|string[]} contexts - One or more context keys
 * @returns {Array} - Array of { header: string } and field objects, suitable for v-autocomplete items
 */
export function getAvailableFieldsGrouped(contexts) {
  const keys = Array.isArray(contexts) ? contexts : [contexts];
  const items = [];

  keys.forEach((ctx) => {
    const ctxConfig = PLACEHOLDER_CONTEXTS[ctx];
    const fields = AVAILABLE_FIELDS[ctx] || [];
    if (fields.length === 0) return;

    // Add context-level divider when multiple contexts
    if (keys.length > 1 && ctxConfig) {
      items.push({ header: ctxConfig.label });
    }

    // Group fields within this context
    const groups = {};
    fields.forEach((f) => {
      const group = f.group || "Sonstige";
      if (!groups[group]) groups[group] = [];
      groups[group].push(f);
    });

    Object.entries(groups).forEach(([group, groupFields]) => {
      const headerLabel = keys.length > 1 && ctxConfig ? `${group}` : group;
      items.push({ header: headerLabel });
      items.push(...groupFields.sort((a, b) => a.label.localeCompare(b.label)));
    });
  });

  return items;
}

/**
 * Builds a hierarchical tree structure from available fields for v-treeview.
 * Each field's `group` property defines its category path (e.g. "Personaldaten > Stammdaten").
 *
 * @param {string|string[]} contexts - One or more context keys
 * @returns {Array} - Tree structure with { id, name, children, field } nodes
 */
export function getAvailableFieldsTree(contexts) {
  const keys = Array.isArray(contexts) ? contexts : [contexts];
  const root = [];

  // Map context to icons for root-level categories
  const contextIcons = {
    organization: "mdi-domain",
    member: "mdi-account",
    personnelContacts: "mdi-card-account-phone",
    dutyReport: "mdi-clipboard-text",
    timeSheet: "mdi-clock-outline",
  };

  keys.forEach((ctx) => {
    const ctxConfig = PLACEHOLDER_CONTEXTS[ctx];
    const fields = AVAILABLE_FIELDS[ctx] || [];
    if (fields.length === 0) return;

    // Create context-level node when multiple contexts
    let contextNode = null;
    if (keys.length > 1 && ctxConfig) {
      contextNode = {
        id: `ctx-${ctx}`,
        name: ctxConfig.label,
        children: [],
        isCategory: true,
        icon: contextIcons[ctx] || "mdi-folder",
      };
      root.push(contextNode);
    }

    // Build hierarchy from group property
    fields.forEach((field) => {
      const group = field.group || "Sonstige";
      const parts = group.split(" > ").map((p) => p.trim());

      let currentLevel = contextNode ? contextNode.children : root;
      let pathSoFar = contextNode ? [ctxConfig.label] : [];

      // Traverse/create hierarchy
      parts.forEach((part, index) => {
        pathSoFar.push(part);
        const isLastPart = index === parts.length - 1;
        let node = currentLevel.find((n) => n.name === part && n.isCategory);

        if (!node) {
          // Determine icon for category
          let icon = "mdi-folder";
          if (index === 0 && !contextNode) {
            // Top-level category without context wrapper
            icon = contextIcons[ctx] || "mdi-folder";
          } else if (part.includes("Kontakt") || part.includes("Adresse")) {
            icon = "mdi-card-account-details";
          } else if (part.includes("Stamm") || part.includes("Person")) {
            icon = "mdi-account-details";
          } else if (part.includes("Dienst")) {
            icon = "mdi-briefcase";
          } else if (part.includes("Zeit")) {
            icon = "mdi-clock";
          } else if (part.includes("Abrechnung")) {
            icon = "mdi-currency-eur";
          } else if (part.includes("Einsatz")) {
            icon = "mdi-fire-truck";
          } else if (part.includes("Teilnehmer")) {
            icon = "mdi-account-group";
          } else if (part.includes("Ausbildung")) {
            icon = "mdi-school";
          }

          node = {
            id: pathSoFar.join(" > "),
            name: part,
            children: [],
            isCategory: !isLastPart,
            icon: icon,
          };
          currentLevel.push(node);
        }

        if (isLastPart) {
          // Add field as leaf node
          node.children.push({
            id: `field-${field.key}`,
            name: field.label,
            description: field.description,
            field: field, // Full field object
            isCategory: false,
          });
        } else {
          currentLevel = node.children;
        }
      });
    });
  });

  return root;
}

/**
 * Gets the full hierarchical path for a field key (e.g. "Personaldaten > Stammdaten > Vorname")
 * @param {string} fieldKey - The field key
 * @returns {string} - The full path or the field label
 */
export function getFieldHierarchyPath(fieldKey) {
  const field = ALL_FIELDS_BY_KEY[fieldKey];
  if (!field) return fieldKey;

  const group = field.group || "";
  return group ? `${group} > ${field.label}` : field.label;
}

/**
 * Detects which data contexts are required based on placeholder mappings.
 * Analyzes all mapped field keys and determines which AVAILABLE_FIELDS context they belong to.
 *
 * @param {Object} placeholderMappings - { [tag]: { fieldKey, status } }
 * @returns {Set<string>} - Set of required context keys (e.g. Set(["member", "organization"]))
 */
export function detectRequiredContexts(placeholderMappings) {
  const required = new Set();
  if (!placeholderMappings) return required;

  Object.values(placeholderMappings).forEach((mapping) => {
    if (mapping.status === "mapped" && mapping.fieldKey) {
      const fieldDef = ALL_FIELDS_BY_KEY[mapping.fieldKey];
      if (fieldDef) {
        required.add(fieldDef.context);
      }
    }
  });

  return required;
}

/**
 * Attempts to auto-map parsed placeholder tags to available data fields.
 * Searches across one or more contexts for matches.
 * Uses exact key match, case-insensitive match, and label-based fuzzy matching.
 *
 * @param {Object} parsedPlaceholders - Output from parsePlaceholders()
 * @param {string|string[]} contexts - One or more placeholder context keys
 * @returns {Object} - Mapping object: { [tag]: { fieldKey, autoMapped, status } }
 */
export function automapPlaceholders(parsedPlaceholders, contexts) {
  const fields = getAvailableFields(contexts);
  const mappings = {};

  // Build lookup tables
  const byKey = {};
  const byKeyLower = {};
  const byLabelLower = {};
  fields.forEach((f) => {
    byKey[f.key] = f;
    byKeyLower[f.key.toLowerCase()] = f;
    const label = f.label.toLowerCase().replace(/[^a-zäöüß0-9]/g, "");
    byLabelLower[label] = f;
  });

  // Map simple placeholders
  const allPlaceholders = [...(parsedPlaceholders.simple || [])];

  // Also map placeholders inside loops
  (parsedPlaceholders.loops || []).forEach((loop) => {
    (loop.placeholders || []).forEach((tag) => {
      if (!allPlaceholders.find((p) => p.tag === tag)) {
        allPlaceholders.push({ tag, isKnown: byKey[tag] !== undefined });
      }
    });
  });

  allPlaceholders.forEach((placeholder) => {
    const tag = placeholder.tag;

    // 1. Exact key match
    if (byKey[tag]) {
      mappings[tag] = {
        fieldKey: byKey[tag].key,
        autoMapped: true,
        status: "mapped",
      };
      return;
    }

    // 2. Case-insensitive key match
    const lower = tag.toLowerCase();
    if (byKeyLower[lower]) {
      mappings[tag] = {
        fieldKey: byKeyLower[lower].key,
        autoMapped: true,
        status: "mapped",
      };
      return;
    }

    // 3. Label-based match (remove spaces/special chars)
    const cleanTag = lower.replace(/[^a-zäöüß0-9]/g, "");
    if (byLabelLower[cleanTag]) {
      mappings[tag] = {
        fieldKey: byLabelLower[cleanTag].key,
        autoMapped: true,
        status: "mapped",
      };
      return;
    }

    // 4. No match – unmapped
    mappings[tag] = {
      fieldKey: null,
      autoMapped: false,
      status: "unmapped",
    };
  });

  return mappings;
}

/**
 * Resolves a dot-path on an object (e.g. "user.address.city" → obj.user.address.city).
 * @param {Object} obj - The source object
 * @param {string} path - Dot-separated path
 * @returns {*} - The resolved value or ""
 */
export function resolvePath(obj, path) {
  if (!obj || !path) return "";
  return (
    path.split(".").reduce((acc, part) => {
      if (acc == null) return "";
      return acc[part];
    }, obj) ?? ""
  );
}

/**
 * Maps a member record from the store to placeholder key-value data.
 * Uses placeholderMappings if provided, otherwise falls back to legacy static mapping.
 * @param {Object} member - Member object from admin.membershipsPublic
 * @param {Object} [placeholderMappings] - Mapping from tag → { fieldKey, status }
 * @returns {Object} - Key-value object for template replacement
 */
export function mapMemberToPlaceholderData(member, placeholderMappings) {
  // If we have explicit mappings, use field-path resolution
  if (placeholderMappings && Object.keys(placeholderMappings).length > 0) {
    return mapRecordWithMappings(member, placeholderMappings);
  }

  // Legacy fallback: static mapping
  const user = member.user || {};
  const displayName = user.displayName || "";
  const nameParts = displayName.split(" ");
  const firstName = user.firstName || nameParts[0] || "";
  const lastName =
    user.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");

  return {
    FirstName: firstName,
    LastName: lastName,
    DisplayName: displayName,
    Rank:
      (member.rank && member.rank.title) ||
      (user.rank && user.rank.title) ||
      "",
    RankShort:
      (member.rank && member.rank.shortTitle) ||
      (user.rank && user.rank.shortTitle) ||
      "",
    Position:
      (member.dutyPosition && member.dutyPosition.title) ||
      (user.dutyPosition && user.dutyPosition.title) ||
      "",
    PositionShort:
      (member.dutyPosition && member.dutyPosition.shortTitle) ||
      (user.dutyPosition && user.dutyPosition.shortTitle) ||
      "",
    Group: (member.group && member.group.title) || "",
    Division:
      (member.division && member.division.title) ||
      (user.division && user.division.title) ||
      "",
    PersonnelNumber: user.personnelNumber || "",
    MemberSince: user.memberSince || "",
  };
}

/**
 * Maps a record to placeholder data using explicit field mappings.
 * Resolves field values using ALL_FIELDS_BY_KEY (cross-context).
 * Custom/unmapped tags are left blank (to be filled by user at generation time).
 *
 * @param {Object} record - The data record (or merged record with data from multiple contexts)
 * @param {Object} placeholderMappings - { [tag]: { fieldKey, status } }
 * @returns {Object} - Key-value object for template replacement
 */
export function mapRecordWithMappings(record, placeholderMappings) {
  const data = {};
  Object.entries(placeholderMappings).forEach(([tag, mapping]) => {
    if (mapping.status === "mapped" && mapping.fieldKey) {
      const fieldDef = ALL_FIELDS_BY_KEY[mapping.fieldKey];
      if (fieldDef) {
        data[tag] = resolvePath(record, fieldDef.path);
      } else {
        data[tag] = "";
      }
    }
    // "custom" and "unmapped" tags are not set here – they come from extraData
  });
  return data;
}

/**
 * Maps records to placeholder data based on the context type.
 * For contexts with explicit mappings, uses mapRecordWithMappings (cross-context).
 * Falls back to legacy mapMemberToPlaceholderData for "member" without mappings.
 *
 * @param {string} context - The primary placeholder context (e.g. "member")
 * @param {Array} records - Array of records from the store
 * @param {Object} [placeholderMappings] - Optional mapping from tag → { fieldKey, status }
 * @returns {Array} - Array of key-value objects for template replacement
 */
export function mapRecordsToPlaceholderData(
  context,
  records,
  placeholderMappings,
) {
  if (!records || !Array.isArray(records)) return [];

  // If we have explicit mappings, use cross-context resolution for all record types
  if (placeholderMappings && Object.keys(placeholderMappings).length > 0) {
    return records.map((r) => mapRecordWithMappings(r, placeholderMappings));
  }

  // Legacy fallback for member context without mappings
  switch (context) {
    case "member":
      return records.map((r) => mapMemberToPlaceholderData(r));
    default:
      console.warn(`No mappings provided for context: ${context}`);
      return [];
  }
}

/**
 * Fetches a .docx template file from Firebase Storage and returns it as an ArrayBuffer.
 * Uses the Firebase Storage SDK (getBytes) to avoid CORS issues.
 * @param {string} filePathOrUrl - The storage path (e.g. 'organizations/.../v1/file.docx') or download URL
 * @returns {Promise<ArrayBuffer>} - The template file as ArrayBuffer
 */
export async function fetchTemplate(filePathOrUrl) {
  // If it's already a full URL, fall back to XHR
  if (filePathOrUrl.startsWith("http")) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = "arraybuffer";
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response);
        } else {
          reject(new Error(`Fehler beim Laden der Vorlage: ${xhr.statusText}`));
        }
      };
      xhr.onerror = () =>
        reject(new Error("Netzwerkfehler beim Laden der Vorlage"));
      xhr.open("GET", filePathOrUrl);
      xhr.send();
    });
  }

  // Use Firebase Storage SDK to download directly (no CORS issues)
  const { ref, getBytes } = await import("firebase/storage");
  const { storage } = await import("@/firebase.js");
  const fileRef = ref(storage._delegate, filePathOrUrl);
  return getBytes(fileRef);
}

/**
 * Processes a .docx template with data and returns the result as a Blob.
 *
 * Supports two modes:
 * 1. Loop mode: Template contains {#Members}...{/Members} loop tags.
 *    Each record creates one iteration of the loop content.
 * 2. Single mode: Template contains direct {Tag} placeholders.
 *    Only the first record is used.
 *
 * Global data (e.g. organization fields) is merged into the top-level data object.
 *
 * @param {ArrayBuffer} templateBuffer - The .docx template as ArrayBuffer
 * @param {string} context - The primary placeholder context (e.g. "member")
 * @param {Array} records - Array of records from the store
 * @param {Object} extraData - Additional global data (e.g. custom fields, organization data)
 * @param {Object} [placeholderMappings] - Optional mapping from tag → { fieldKey, status }
 * @param {Object} [globalData] - Global data objects keyed by context (e.g. { organization: {...} })
 * @returns {Promise<Blob>} - The processed document as Blob
 */
export async function generateDocument(
  templateBuffer,
  context,
  records,
  extraData = {},
  placeholderMappings,
  globalData = {},
) {
  const contextConfig = PLACEHOLDER_CONTEXTS[context];
  if (!contextConfig) {
    throw new Error(`Unbekannter Platzhalter-Kontext: ${context}`);
  }

  const mappedRecords = mapRecordsToPlaceholderData(
    context,
    records,
    placeholderMappings,
  );

  // Resolve global context fields (e.g. organization data)
  const globalFields = {};
  if (placeholderMappings) {
    Object.entries(placeholderMappings).forEach(([tag, mapping]) => {
      if (mapping.status === "mapped" && mapping.fieldKey) {
        const fieldDef = ALL_FIELDS_BY_KEY[mapping.fieldKey];
        if (fieldDef && PLACEHOLDER_CONTEXTS[fieldDef.context]?.isGlobal) {
          const sourceData = globalData[fieldDef.context];
          if (sourceData) {
            globalFields[tag] = resolvePath(sourceData, fieldDef.path);
          }
        }
      }
    });
  }

  // Build template data
  const data = {
    ...extraData,
    ...globalFields,
    Date: new Date().toLocaleDateString("de-DE"),
    // Also provide flat data from first record for single-record templates
    ...(mappedRecords.length > 0 ? mappedRecords[0] : {}),
  };

  // Add loop data for each context that has a loopTag
  if (contextConfig.loopTag) {
    data[contextConfig.loopTag] = mappedRecords;
  }

  const handler = new TemplateHandler();
  const doc = await handler.process(templateBuffer, data);

  return doc;
}

/**
 * Triggers a browser download of a Blob.
 * @param {Blob} blob - The file blob to download
 * @param {string} filename - The download filename
 */
export function downloadBlob(blob, filename) {
  // Ensure we have a proper Blob (handler.process returns ArrayBuffer)
  if (!(blob instanceof Blob)) {
    blob = new Blob([blob], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Full pipeline: fetch template, process with data, trigger download.
 * @param {string} filePathOrUrl - The storage path or download URL of the .docx template
 * @param {string} context - The placeholder context (e.g. "member")
 * @param {Array} records - Array of records from the store
 * @param {string} outputFilename - The download filename
 * @param {Object} extraData - Additional global data
 * @param {Object} [placeholderMappings] - Optional mapping from tag → { fieldKey, status }
 * @param {Object} [globalData] - Global data objects keyed by context (e.g. { organization: {...} })
 * @returns {Promise<void>}
 */
export async function processAndDownload(
  filePathOrUrl,
  context,
  records,
  outputFilename,
  extraData = {},
  placeholderMappings,
  globalData = {},
) {
  const templateBuffer = await fetchTemplate(filePathOrUrl);
  const resultBlob = await generateDocument(
    templateBuffer,
    context,
    records,
    extraData,
    placeholderMappings,
    globalData,
  );
  downloadBlob(resultBlob, outputFilename);
}

/**
 * Parses a .docx template file and extracts all placeholder tags.
 * Uses TemplateHandler.parseTags to extract {Tag}, {#Loop}, {/Loop}, and image placeholders.
 *
 * @param {File|ArrayBuffer} templateFile - The .docx file (File object or ArrayBuffer)
 * @param {string|string[]} placeholderContexts - One or more placeholder context keys
 * @returns {Promise<Object>} - Object with structure:
 *   {
 *     simple: [{ tag: "FirstName", isKnown: true }, ...],
 *     loops: [{ tag: "Members", placeholders: ["FirstName", "LastName"] }],
 *     images: [{ tag: "Photo" }]
 *   }
 */
export async function parsePlaceholders(templateFile, placeholderContexts) {
  // Convert File to ArrayBuffer if needed
  let buffer = templateFile;
  if (templateFile instanceof File || templateFile instanceof Blob) {
    buffer = await templateFile.arrayBuffer();
  }

  const handler = new TemplateHandler();
  const tags = await handler.parseTags(buffer);

  // Build known tags set across all provided contexts
  const contexts = Array.isArray(placeholderContexts)
    ? placeholderContexts
    : [placeholderContexts];
  const knownTags = new Set();
  contexts.forEach((ctx) => {
    const contextConfig = PLACEHOLDER_CONTEXTS[ctx];
    if (contextConfig && contextConfig.placeholders) {
      contextConfig.placeholders.forEach((p) => knownTags.add(p.tag));
    }
  });

  // Classify tags
  const simple = [];
  const loops = [];
  const images = [];

  console.log("Parsed tags:", tags);

  let currentLoop = null;

  tags.forEach((tag) => {
    // Strip curly braces from tag name (parseTags returns e.g. "{#Members}" not "#Members")
    const tagName = (tag.rawText || tag.text || "")
      .replace(/^\{/, "")
      .replace(/\}$/, "")
      .trim();

    // Check for loop tags
    if (tagName.startsWith("#")) {
      const loopName = tagName.substring(1);
      currentLoop = {
        tag: loopName,
        placeholders: [],
      };
      loops.push(currentLoop);
    } else if (tagName.startsWith("/")) {
      // Closing loop tag - end current loop scope
      currentLoop = null;
    } else if (tag.type === "image") {
      // Image placeholder
      images.push({ tag: tagName });
    } else {
      // Simple placeholder
      const isKnown = knownTags.has(tagName);
      const placeholder = {
        tag: tagName,
        isKnown: isKnown,
        required: !isKnown, // Unknown tags are custom and required
      };
      simple.push(placeholder);

      // If inside a loop, also add to loop's placeholders
      if (currentLoop) {
        currentLoop.placeholders.push(tagName);
      }
    }
  });

  return {
    simple,
    loops,
    images,
  };
}
